import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../utils/contexts/UserContext";
import TeacherDirectory from "../../components/TeacherDirectory/TeacherDirectory";
import SchoolMatrix from "./SchoolMatrix";
import Skeleton from "../../components/Skeleton/Skeleton";
import RubricGate, { isRubricGated } from "../../components/RubricGate/RubricGate";
import useModalsStore from "../../utils/stores/useModalsStore";
import useOrgScope from "../../utils/hooks/useOrgScope";
import { getActiveRubric } from "../../services/rubricService";
import {
  getSchoolMatrix,
  getTeachersAnalytics,
} from "../../services/analyticsService";
import { COUNTED_OBSERVATION_LIMIT } from "../../utils/helpers/scoring";
import {
  DASHBOARD_PERMISSION_CODES,
  hasPermission,
} from "../../utils/permissions";
import { scoreHex, scoreTierLegend } from "../../utils/helpers/scoreColors";

export default function Heatmap() {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const canView = hasPermission(user, DASHBOARD_PERMISSION_CODES.SCHOOL_VIEW);
  const { effectiveOrgUuid, scopeMode, setSelectedOrgUuid } = useOrgScope();
  const openTeacherOverview = useModalsStore((s) => s.openTeacherOverview);

  const [rubric, setRubric] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [activeRubrics, setActiveRubrics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!canView || scopeMode === "loading") return undefined;
    // With the subtree unknown neither branch can be trusted, so say the load
    // failed instead of drawing the gated empty roster as "no teachers".
    if (scopeMode === "unavailable") {
      setLoadError(t("heatmap.loadError"));
      setIsLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    const { signal } = controller;
    setIsLoading(true);
    setLoadError("");

    const request = scopeMode === "network"
      ? // A network reads schools, never teachers: one row per school instead
        // of thousands of rows, and the rubric descriptor rides along, so the
        // multi-rubric gate is also decided without the roster.
        getSchoolMatrix({
          params: { organization: effectiveOrgUuid },
          signal,
        }).then((data) => {
          if (signal.aborted) return;
          setMatrix(data);
          setTeachers([]);
          setActiveRubrics(data?.active_rubrics || null);
        })
      : Promise.all([
          // The selected school's rubric, not the caller's own — a head-org user
          // viewing a child school must see the columns that school is scored on.
          getActiveRubric({
            params: { organization: effectiveOrgUuid },
            signal,
          }),
          getTeachersAnalytics({
            params: { organization: effectiveOrgUuid },
            signal,
          }),
        ]).then(([rubricData, teachersData]) => {
          if (signal.aborted) return;
          setMatrix(null);
          setRubric(rubricData);
          setTeachers(teachersData?.teachers || []);
          setActiveRubrics(teachersData?.active_rubrics || null);
        });

    request
      .then(() => {
        if (signal.aborted) return;
        setIsLoading(false);
      })
      .catch((err) => {
        if (signal.aborted || err?.code === "ERR_CANCELED") return;
        setLoadError(t("heatmap.loadError"));
        setIsLoading(false);
      });
    return () => controller.abort();
  }, [canView, scopeMode, effectiveOrgUuid, t]);

  const standards = useMemo(
    () =>
      [...(rubric?.standards || [])].sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0),
      ),
    [rubric],
  );

  return (
    <div className="p-6 bg-slate-50 min-h-full w-full">
      <div className="max-w-fit mx-auto">
        {/* The school picker lives in the app header now — see BaseLayout. */}
        {isLoading ? (
          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2">
            <Skeleton className="h-8 w-full mb-2" />
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : loadError ? (
          <div className="py-20 text-center text-sm text-rose-600">
            {loadError}
          </div>
        ) : matrix ? (
          // Score and coverage are rubric-agnostic, so a network whose schools
          // run different rubrics still gets its grid — minus the per-standard
          // columns, which the gate above it explains. RubricGate self-hides.
          <>
            <RubricGate activeRubrics={activeRubrics} className="mb-4" />
            <SchoolMatrix matrix={matrix} onSchoolClick={setSelectedOrgUuid} />
          </>
        ) : isRubricGated(activeRubrics) ? (
          <RubricGate activeRubrics={activeRubrics} />
        ) : standards.length === 0 ? (
          <div className="py-20 text-center text-sm text-slate-400">
            {t("heatmap.empty")}
          </div>
        ) : (
          <TeacherDirectory
            standards={standards}
            teachers={teachers}
            onCellClick={(teacher, std) =>
              openTeacherOverview({ teacher, focusStandardUuid: std.uuid })
            }
            onNameClick={(teacher) => openTeacherOverview({ teacher })}
          />
        )}

        {/* The dot/scale legend explains the grid; hide it when the grid is
            replaced by the multi-rubric gate so it doesn't dangle below it.
            The school matrix keeps its score colours through that gate, so it
            keeps the legend too. */}
        {(matrix || !isRubricGated(activeRubrics)) && (
        <div className="mt-6 flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-slate-500 w-full max-w-5xl mx-auto px-4 flex-wrap gap-2">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <span>{t("heatmap.scoreScale")}</span>
              <div className="flex items-center gap-1.5">
                <span>0%</span>
                <div
                  className="w-24 h-1.5 rounded-full"
                  style={{
                    // Built from the SCORE_TIERS palette (low→high) so the scale
                    // bar matches the dots/legend instead of a separate HSL ramp.
                    background: `linear-gradient(to right, ${scoreTierLegend()
                      .map((tier) => tier.hex)
                      .join(", ")})`,
                  }}
                />
                <span>100%</span>
              </div>
            </div>
            {/* Dots are a per-teacher thing — the school matrix has no cell to
                put them in. */}
            {!matrix && (
            <div className="flex items-center gap-3 flex-wrap">
              <span>
                {t("heatmap.dotHint", { count: COUNTED_OBSERVATION_LIMIT })}
              </span>
              <span className="flex items-center gap-1.5">
                <span
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: scoreHex(null) }}
                />
                {t("heatmap.notEvaluated")}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-white ring-1 ring-slate-300" />
                {t("heatmap.noObservation")}
              </span>
            </div>
            )}
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <span>{t("heatmap.overall")}</span>
            {scoreTierLegend().map((tier) => (
              <span key={tier.min} className="flex items-center gap-1.5">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: tier.hex }}
                />{" "}
                {tier.label}
              </span>
            ))}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
